// @flow
import * as React from 'react';

import assertions from 'lib/assertions';
import Button from 'components/Button';
import Container from 'components/Container';
import EventTracking from 'lib/EventTracking';
import FormElement from 'components/FormElement';
import Heading from 'components/Heading';
import Input from 'components/Input';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Text from 'components/Text';
import View from 'components/View';
import WizardHelperText from 'components/WizardHelperText';
import WizardPageLayout from 'components/WizardPageLayout';

import type { AutoMessageTabKey } from './';
import type { SaveAutoMessageInput } from './queries/__generated__/AdminNewAutoMessageOrgs';

type Props = {
  csvName: ?string,
  onChangeField: () => void,
  onAdvance: () => void,
  onSave: (fields: SaveAutoMessageInput, onSuccess: Function) => void,
  onJump: (key: AutoMessageTabKey) => void,
};

type State = {
  csvNameFromField: ?string,
};

class AdminNewAutoMessageFormatCSV extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      csvNameFromField: props.csvName,
    };
  }

  componentDidMount() {
    EventTracking.trackKey('auto_messaging.format_csv.view');
    if (this.props.csvName) {
      this.setCsvNameField(this.props.csvName);
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.csvName && this.props.csvName) {
      this.setCsvNameField(this.props.csvName);
    }
  }

  setCsvNameField = (csvName: ?string) => {
    this.setState({ csvNameFromField: csvName });
  };

  handleCsvNameChange = (csvName: ?string) => {
    this.setCsvNameField(csvName);
    this.props.onChangeField();
  };

  render() {
    const { onSave } = this.props;
    const { csvNameFromField } = this.state;

    return (
      <WizardPageLayout
        actionButtons={[
          <Button
            data-test="continue-button"
            onClick={() => onSave({ csvName: csvNameFromField }, this.props.onAdvance)}
            disabled={!csvNameFromField}>
            Save and continue
          </Button>,
        ]}>
        <View>
          <Row size="xlarge">
            <Container flush size="large">
              <Text align="left" lineHeight="large">
                <Row size="small">
                  <Heading level="2">
                    <Text weight="semibold">Set up CSV Exports</Text>
                  </Heading>
                </Row>
                <MediaObject center={false} figure="right">
                  <Container flush size="large">
                    <Row size="small">
                      Each auto message requires student numbers as well as any custom data that you
                      want to include. You’ll need to export this data from your SIS as a CSV file.
                    </Row>
                    <Row size="small">
                      <Button href="/assets/csv/student_number_list.csv" level="secondary">
                        Download template
                      </Button>
                    </Row>
                    <Row size="medium">
                      In the next step, you’ll transfer your exported CSV to Remind via SFTP. For
                      now, enter the name of your CSV.
                    </Row>
                    <FormElement
                      label="CSV filename"
                      labelNote="This should be a unique filename. You can view a timestamped log of transfer activity for this message."
                      onValidChange={e => this.handleCsvNameChange(e.target.value)}
                      onInvalidChange={() => this.handleCsvNameChange(undefined)}
                      validate={e => {
                        assertions.endsWithExactString(
                          e.target.value,
                          '.csv',
                          'Your filename must include .csv at the end of it'
                        );
                        assertions.noSpaces(
                          e.target.value,
                          'Your filename cannot include any spaces'
                        );
                      }}
                      element={Input}
                      elementProps={{
                        defaultValue: csvNameFromField,
                        placeholder: 'unexcusedabsences.csv, lowlunchbalance.csv',
                      }}
                    />
                  </Container>
                  <WizardHelperText
                    text="Not ready to export reports from your SIS?"
                    linkLabel="Skip to compose"
                    onClickLink={() => this.props.onJump('compose')}
                  />
                </MediaObject>
              </Text>
            </Container>
          </Row>
        </View>
      </WizardPageLayout>
    );
  }
}

export default AdminNewAutoMessageFormatCSV;
